import React, { useEffect } from 'react'
import { m } from 'framer-motion'
import Navigation from '../Restaurant/components/Navigation'
import Footer from '../Restaurant/components/Footer'
import StickyCTA from '../Restaurant/components/StickyCTA'
import Press from '../Restaurant/components/Press'
import LocalIntentSection from '../Restaurant/components/LocalIntentSection'
import { useCMS } from '../../context/CMSContext'
import { useLanguage } from '../../context/LanguageContext'
import { useTheme } from '../../context/ThemeContext'
import { getImageUrl } from '../../utils/imageUtils'
import '../../styles/components/restaurant.css'

interface StorySection {
    kicker: string
    title: string
    body: string
}

const StoryPage: React.FC = () => {
    const { content } = useCMS()
    const { t, language } = useLanguage()
    const { theme } = useTheme()

    // 라우트 진입 시 상단으로
    useEffect(() => {
        window.scrollTo({ top: 0 })
    }, [])

    const headerImage = getImageUrl('/images/naver_1762994586149_hero.webp')

    const heading =
        language === 'ko'
            ? { eyebrow: 'Our Story', title: '1931, 삼겹살의 원형을 복원하다', lead: '‘세겹살’이라는 이름이 처음 기록된 1931년. 그 시절의 삼겹살은 흑돈(흑돼지)이었습니다. 흑돈1931은 그 원형을 오늘의 식탁에 되살립니다.' }
            : { eyebrow: 'Our Story', title: 'Restoring the Origin of Samgyeopsal, Since 1931', lead: 'In 1931, the name “samgyeopsal” was first recorded — and back then, it was black pork. BlackPork1931 brings that original form back to the table.' }

    const sections: StorySection[] = [
        { kicker: '01', title: t('intro.sections.promise.title'), body: t('intro.sections.promise.content') },
        { kicker: '02', title: t('intro.sections.philosophy.title'), body: t('intro.sections.philosophy.content') },
        { kicker: '03', title: t('intro.sections.aging.title'), body: t('intro.sections.aging.content') },
        { kicker: '04', title: t('intro.sections.recognition.title'), body: t('intro.sections.recognition.content') },
    ]

    const breeds =
        language === 'ko'
            ? [
                  { name: '버크셔K 삼겹살', origin: '전북 남원 · 다산육종', desc: '박화춘 박사가 개량한 품종. 달큰하고 고소한 지방과 부드러운 육질.' },
                  { name: '우리흑돈 목살', origin: '경북 경산 · 덕유농장', desc: '재래돼지의 맛을 잇는 국산 흑돈. 탄탄한 조직과 풍부한 육즙.' },
              ]
            : [
                  { name: 'Berkshire K Belly', origin: 'Namwon, Jeonbuk', desc: 'A refined breed with sweet, nutty fat and tender texture.' },
                  { name: 'Woori Heukdon Neck', origin: 'Gyeongsan, Gyeongbuk', desc: 'Korean native black pork — firm texture, rich juices.' },
              ]

    const timeline =
        language === 'ko'
            ? [
                  { year: '1931', label: '‘세겹살’이 방신영 『조선요리제법』에 처음 기록됨' },
                  { year: 'Origin', label: '그 시절 삼겹살은 백돼지가 아닌 재래종 흑돈(흑돼지)이었다' },
                  { year: 'Today', label: '버크셔K·우리흑돈을 160시간 이상 숙성해 원형을 복원' },
                  { year: 'Certified', label: '개업 후 3~4개월 만에 한돈인증점 획득' },
              ]
            : [
                  { year: '1931', label: '“Samgyeopsal” first recorded in Bang Sin-yeong’s cookbook' },
                  { year: 'Origin', label: 'Back then, samgyeopsal was native black pork — not white pork' },
                  { year: 'Today', label: 'Berkshire K & Woori Heukdon, aged 160+ hours' },
                  { year: 'Certified', label: 'Earned Handon certification within months of opening' },
              ]

    return (
        <div className={`restaurant-page story-page theme-${theme.mode}`}>
            <Navigation />

            {/* Header */}
            <header className="story-hero">
                <div className="story-hero-bg" style={{ backgroundImage: `url(${headerImage})` }} />
                <div className="story-hero-overlay" />
                <m.div
                    className="story-hero-content"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="story-eyebrow">{heading.eyebrow}</span>
                    <h1 className="story-hero-title">{heading.title}</h1>
                    <p className="story-hero-lead">{heading.lead}</p>
                </m.div>
            </header>

            {/* Timeline */}
            <section className={`story-section theme-${theme.mode}`}>
                <div className="story-wrap">
                    <ol className="story-timeline">
                        {timeline.map((it, i) => (
                            <m.li
                                key={i}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                            >
                                <span className="story-timeline-year">{it.year}</span>
                                <span className="story-timeline-label">{it.label}</span>
                            </m.li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Story prose */}
            <section className={`story-section alt theme-${theme.mode}`}>
                <div className="story-wrap story-prose">
                    {sections.map((s, i) => (
                        <m.article
                            key={i}
                            className="story-block"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                        >
                            <span className="story-block-kicker">{s.kicker}</span>
                            <h2 className="story-block-title">{s.title}</h2>
                            <p className="story-block-body">{s.body}</p>
                        </m.article>
                    ))}
                </div>
            </section>

            {/* Breeds */}
            <section className={`story-section theme-${theme.mode}`}>
                <div className="story-wrap">
                    <span className="story-eyebrow story-eyebrow-dark">{language === 'ko' ? 'Breeds' : 'Breeds'}</span>
                    <h2 className="story-section-title">{language === 'ko' ? '두 품종의 정직한 조화' : 'A Pairing of Two Breeds'}</h2>
                    <div className="story-breeds">
                        {breeds.map((b, i) => (
                            <m.div
                                key={i}
                                className="story-breed"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                            >
                                <h3>{b.name}</h3>
                                <span className="story-breed-origin">{b.origin}</span>
                                <p>{b.desc}</p>
                            </m.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 언론보도 — 한국어에서만 */}
            {language === 'ko' && content.press && <Press content={content.press} />}

            {/* 예약 클로징 */}
            <LocalIntentSection />

            {content.footer && <Footer content={content.footer} />}
            <StickyCTA />
        </div>
    )
}

export default StoryPage
